import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dataService from 'services/dataService';
import { API_PODS } from 'services/dataService/resources';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import { ApiError } from '../utils/api-error';
import { classifyPodError } from '../utils/pod-error-classifier';

import type { CreatePodResponse } from '../types';
import type { ICreatePodFormValues } from '../components/create-pod-modal/validation-schema';

interface ICreatePodPayload {
  agencyGuid?: string;
  podType: 'RPM' | 'CCM';
  podName: string;
  status: 'active' | 'inactive';
  states: string[];
  clinicGuids: string[];
  members: Array<{ loginGuid: string; priority: string }>;
}

interface IUseCreatePodOptions {
  onSuccess?: (podGuid: string) => void;
  onDuplicateName?: (message: string) => void;
}

const mapFormToPayload = (values: ICreatePodFormValues): ICreatePodPayload => ({
  ...(values.agencyGuid && { agencyGuid: values.agencyGuid }),
  podType: values.podType,
  podName: values.podName,
  status: values.isActive ? 'active' : 'inactive',
  states: values.states,
  clinicGuids: values.clinicGuids.map((c) => c.guid),
  members: values.members.map((m) => ({
    loginGuid: m.loginGuid,
    priority: m.priority,
  })),
});

export const useCreatePod = ({
  onSuccess,
  onDuplicateName,
}: IUseCreatePodOptions = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createPod, isLoading } = useMutation({
    mutationFn: async (values: ICreatePodFormValues) => {
      const payload = mapFormToPayload(values);
      const { data, error, status } =
        await dataService.createOne<CreatePodResponse>(API_PODS, payload);
      if (error) {
        throw new ApiError(error, status);
      }
      return data;
    },
    onSuccess: async (data) => {
      notificationController.success({
        message: t('labels.podCreatedSuccessfully'),
      });

      // Surface non-blocking advisories
      if (data?.advisories?.length) {
        const primaryPattern = /primary.*assignment.*another.*active.*pod/i;
        const primaryAdvisories: typeof data.advisories = [];
        const otherAdvisories: typeof data.advisories = [];

        data.advisories.forEach((a) => {
          (primaryPattern.test(a.message)
            ? primaryAdvisories
            : otherAdvisories
          ).push(a);
        });

        if (primaryAdvisories.length > 1) {
          notificationController.info({
            message: t('labels.memberPrimaryAssignmentAdvisory'),
          });
        } else if (primaryAdvisories.length === 1) {
          notificationController.info({
            message: primaryAdvisories[0].message,
          });
        }

        otherAdvisories.forEach((advisory) => {
          notificationController.info({ message: advisory.message });
        });
      }

      // Invalidate pod list and detail caches to trigger refetch.
      // Wait for the list refetch to finish before notifying the caller so the
      // new row is already rendered when the highlight starts.
      queryClient.invalidateQueries({ queryKey: [API_PODS, 'detail'] });
      await queryClient.invalidateQueries({ queryKey: [API_PODS, 'list'] });

      onSuccess?.(data?.pod?.guid ?? '');
    },
    onError: (error: unknown) => {
      const classified = classifyPodError(error);

      if (classified.type === 'DUPLICATE_POD_NAME') {
        const msg = classified.messageKey
          ? t(classified.messageKey, classified.fallback)
          : classified.fallback;
        if (onDuplicateName) {
          onDuplicateName(msg);
        } else {
          notificationController.error({ message: msg });
        }
        return;
      }

      if (classified.type === 'INSUFFICIENT_PRIVILEGES') {
        notificationController.error({
          message: classified.messageKey
            ? t(classified.messageKey, classified.fallback)
            : classified.fallback,
        });
        return;
      }

      // VALIDATION_ERROR (400) and GENERIC (500+/unknown):
      notificationController.error({
        message: classified.messageKey
          ? t(classified.messageKey, classified.fallback)
          : classified.fallback,
      });
    },
  });

  return { createPod, isLoading };
};
